import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { IonAlert, IonButton, IonIcon, IonItem, IonLabel, IonList, IonRadio, IonRadioGroup } from '@ionic/react';
import { trashBinOutline } from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';
import { CapSquarePayments } from 'cap-square-payment';
import { getConfig } from '../../appConfig';
import Basket from '../../lib/basket';
import { Spacer, StrongText, NormalText, Title, Subtitle } from '../common';
import { ReactComponent as CardDefault } from '../../assets/images/card-default.svg';
import Modal from '../modal';
import './styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { getPaymentCards, removePaymentCard, saveSquarePaymentCard } from '../../store/orders/actions';
import { loading, showToast } from '../../store/actions';
import { verifySquareBuyerNative } from '../../lib/utils';


let squareInitPromise = null;
let cardsFetched = false;
let googlePayScriptLoaded = false;

const loadGooglePayScript = () => {
	if (googlePayScriptLoaded || window.google?.payments?.api) {
		return Promise.resolve();
	}
	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = 'https://pay.google.com/gp/p/js/pay.js';
		script.onload = () => {
			googlePayScriptLoaded = true;
			resolve();
		};
		script.onerror = () => reject(new Error('Failed to load Google Pay script'));
		document.head.appendChild(script);
	});
};

const SquarePayButtons = ({
	__,
	disabled = false,
	createOrderCb = null,
	autoSaveCard = false,
	restaurant = null,
	totalPrice = null,
	hideGooglePay = false,
	hideApplePay = false,
	hideCardPayment = false,
	onBeforePay = null
}) => {

	const dispatch = useDispatch();
	const profile = useSelector((state) => state.profile.profile);
	const cards = useSelector((state) => state.orders.cards);
	const clientProfile = useSelector((state) => state.common.clientProfile);
	const [internalSelectedCard, setInternalSelectedCard] = useState(null);
	const [hasAutoSelected, setHasAutoSelected] = useState(false);
	const [saveCardModalOpen, setSaveCardModalOpen] = useState(false);
	const [removeCardAlert, setRemoveCardAlert] = useState(null);
	const [applePayAvailable, setApplePayAvailable] = useState(false);
	const [googlePayAvailable, setGooglePayAvailable] = useState(false);
	const [googlePayButtonReady, setGooglePayButtonReady] = useState(false);
	const googlePayContainerRef = useRef(null);
	useEffect(() => {
		const squareConfig = getConfig()?.squarePayment;
		if (!squareConfig?.enabled) {
			return;
		}

		if (!cardsFetched) {
			cardsFetched = true;
			dispatch(getPaymentCards());
		}

		const checkPaymentAvailability = async () => {
			if (!squareInitPromise) {
				squareInitPromise = CapSquarePayments.initialize({
					applicationId: squareConfig?.applicationId || "sandbox-sq0idb-wY_4L4ft9v_TWlGfrzba9Q",
					locationId: restaurant?.business_location_id
				}).catch((error) => {
					console.error('Square initialization error:', error);
					squareInitPromise = null;
					throw error;
				});
			}
			try {
				await squareInitPromise;
			} catch {
				return;
			}

			const platform = Capacitor.getPlatform();
			if (!hideApplePay) {
				const { applePayOnNative, applePayOnWeb, merchantId, countryCode, currencyCode } = squareConfig;
				const mightHaveApple =
					(platform === 'ios' && applePayOnNative) ||
					(platform === 'web' && applePayOnWeb);

				if (mightHaveApple) {
					try {
						const { canUseApplePay } = await CapSquarePayments.canUseApplePay();
						if (canUseApplePay) {
							await CapSquarePayments.initializeApplePay({
								merchantId: merchantId || 'merchant.com.hybriddirectdev',
								countryCode: countryCode || 'GB',
								currencyCode: currencyCode || 'GBP'
							});
							setApplePayAvailable(true);
						}
					} catch (error) {
						console.error('Apple Pay initialization error:', error);
					}
				}
			}

			if (!hideGooglePay) {
				const { googlePayOnNative, googlePayOnWeb, countryCode, currencyCode } = squareConfig;
				const mightHaveGoogle =
					(platform === 'android' && googlePayOnNative) ||
					(platform === 'web' && googlePayOnWeb);

				if (mightHaveGoogle) {
					try {
						const { canUseGooglePay } = await CapSquarePayments.canUseGooglePay();
						if (canUseGooglePay) {
							await CapSquarePayments.initializeGooglePay({
								locationId: restaurant?.business_location_id,
								countryCode: countryCode || 'GB',
								currencyCode: currencyCode || 'GBP'
							});
							setGooglePayAvailable(true);
						}
					} catch (error) {
						console.error('Google Pay initialization error:', error);
					}
				}
			}
		};

		checkPaymentAvailability();
	}, []);

	useEffect(() => {
		if (!hasAutoSelected && cards && cards.length > 0 && !internalSelectedCard) {
			const firstCardId = cards[0].id;
			setInternalSelectedCard(firstCardId);
			Basket.changeSelectedCard(firstCardId);
			setHasAutoSelected(true);
		}
	}, [cards]);

	// Details Square needs to run 3D Secure for this payment.
	// Returns null when squarePayment.threeDSecureEnabled is off, which skips 3D Secure entirely.
	const getVerificationDetails = (intent) => {
		const squareConfig = getConfig()?.squarePayment;
		if (!squareConfig?.threeDSecureEnabled) {
			return null;
		}
		return {
			// Square rejects amount and currencyCode for the STORE intent, only a CHARGE carries them.
			...(intent === 'CHARGE' && {
				amount: Number(totalPrice || Basket.getTotal()).toFixed(2),
				currencyCode: squareConfig?.currencyCode || 'GBP'
			}),
			intent,
			billingContact: {
				givenName: profile?.first_name || undefined,
				familyName: profile?.last_name || undefined,
				email: profile?.email || undefined,
				countryCode: squareConfig?.countryCode || 'GB'
			}
		};
	};

	// Native only: Square location the verification runs against.
	// The subscription checkout uses a placeholder location, so native verification is skipped there.
	const getVerificationLocationId = () => {
		const locationId = restaurant?.business_location_id;
		return locationId && locationId !== 'subscriptions' ? locationId : null;
	};

	const handleCardChange = (cardId) => {
		setInternalSelectedCard(cardId);
		Basket.changeSelectedCard(cardId);
	};

	const handlePayWithSelectedCard = () => {
		if (onBeforePay && onBeforePay() === false) return;
		if (createOrderCb) {
			createOrderCb({
				selectedCard: { id: internalSelectedCard },
				nonce: internalSelectedCard,
				isSelectedCard: true,
				isApplePay: false,
				isGooglePay: false,
				saveCard: false,
				chargeVerificationDetails: getVerificationDetails('CHARGE'),
				verificationLocationId: getVerificationLocationId()
			});
		}

	};

	const handleApplePayClick = async () => {
		if (!applePayAvailable) {
			return;
		}
		if (onBeforePay && onBeforePay() === false) return;

		try {
			const totalAmount = parseInt(totalPrice || Basket.getTotal() * 100);
			const appName = clientProfile?.buisiness_name || 'App';
			const merchantName = restaurant?.name || 'Total';
			const totalLabel = `${merchantName} (via ${appName})`;
			const result = await CapSquarePayments.startApplePay({
				totalAmount: totalAmount,
				totalLabel: totalLabel,
				lineItems: [
					{ label: 'Total', amount: totalAmount }
				]
			});
			if (result?.nonce) {
				if (createOrderCb) {
					createOrderCb({
						selectedCard: result.card,
						nonce: result.nonce,
						isSelectedCard: false,
						isApplePay: true,
						isGooglePay: false,
						saveCard: false
					});
				} else {
					console.warn('createOrderCb is not defined');
				}
			}
		} catch (error) {
			console.error('Apple Pay Error:', error);
		}
	};

	const handleGooglePayClick = async () => {
		if (!googlePayAvailable) {
			return;
		}
		if (onBeforePay && onBeforePay() === false) return;
		dispatch(loading(true));
		try {
			const totalAmount = parseInt((totalPrice || Basket.getTotal()) * 100);
			const appName = clientProfile?.buisiness_name || 'App';
			const result = await CapSquarePayments.startGooglePay({
				totalAmount: totalAmount,
				merchantName: restaurant?.name || 'Total',
				totalLabel: `${restaurant?.name || 'Total'} (via ${appName})`,
				lineItems: [
					{ label: 'Total', amount: totalAmount }
				]
			});
			if (result?.nonce) {
				if (createOrderCb) {
					createOrderCb({
						selectedCard: result.card,
						nonce: result.nonce,
						isSelectedCard: false,
						isApplePay: false,
						isGooglePay: true,
						saveCard: false
					});
				}
			}
		} catch (error) {
			console.error('Google Pay Error:', error);
		}
		dispatch(loading(false));
	};

	const renderGooglePayButton = useCallback((container) => {
		if (!container || !googlePayAvailable || googlePayButtonReady) return;
		googlePayContainerRef.current = container;

		const squareConfig = getConfig()?.squarePayment;
		const isSandbox = squareConfig?.applicationId?.startsWith('sandbox-') || false;

		loadGooglePayScript().then(() => {
			if (!window.google?.payments?.api) return;
			const client = new window.google.payments.api.PaymentsClient({
				environment: isSandbox ? 'TEST' : 'PRODUCTION'
			});
			const button = client.createButton({
				onClick: handleGooglePayClick,
				buttonColor: squareConfig?.googleButtonColor || 'black',
				buttonSizeMode: 'fill',
				buttonType: 'pay',
				buttonRadius: 30
			});
			container.innerHTML = '';
			container.appendChild(button);
			setGooglePayButtonReady(true);
		}).catch((error) => {
			console.error('Failed to render Google Pay button:', error);
		});
	}, [googlePayAvailable, googlePayButtonReady]);

	const handleNewCard = async (saveCard) => {
		setSaveCardModalOpen(false);
		let cardFromUser;
		try {
			cardFromUser = await getCardFromUser(saveCard);
		} catch (error) {
			// Closing the card form is reported as an error by the plugin, it is not a failure.
			if (error?.code === 'CANCELED' || /cancel/i.test(error?.message || '')) {
				return;
			}
			console.error('Square card entry error:', error);
			dispatch(showToast(__('Unable to open the card form. Please try again.'), 'danger'));
			return;
		}
		if (cardFromUser && cardFromUser?.nonce) {
			const isNative = Capacitor.isNativePlatform();
			const verificationLocationId = getVerificationLocationId();
			// Native card entry returns an unverified nonce, so a card that will be saved is verified here.
			// On web the nonce already carries the verification.
			let verificationToken = null;
			const storeVerificationDetails = isNative && saveCard && verificationLocationId ? getVerificationDetails('STORE') : null;
			if (storeVerificationDetails) {
				try {
					verificationToken = await verifySquareBuyerNative({
						sourceId: cardFromUser.nonce,
						locationId: verificationLocationId,
						verificationDetails: storeVerificationDetails
					});
				} catch (error) {
					console.error('Square card verification error:', error);
					dispatch(showToast(__('Card verification failed. Please try again or use another card.'), 'danger'));
					return;
				}
			}
			// With 3D Secure on, a verified new card is only saved here. The buyer then pays with it from
			// the list, which runs the verification for the charge, instead of two challenges in a row.
			const cardVerified = isNative ? !!verificationToken : !!getVerificationDetails('STORE');
			// Guests have no saved cards list to pay from, so they keep paying straight away.
			if (saveCard && cardVerified && !profile.is_guest) {
				dispatch(saveSquarePaymentCard(
					{ nonce: cardFromUser.nonce, card: cardFromUser.card, verificationToken },
					{ cb: (savedCard) => savedCard?.id && handleCardChange(savedCard.id) }
				));
				return;
			}
			if (createOrderCb) {
				createOrderCb({
					selectedCard: cardFromUser.card,
					nonce: cardFromUser.nonce,
					isSelectedCard: false,
					isApplePay: false,
					isGooglePay: false,
					saveCard: saveCard,
					verificationToken,
					// A saved card is charged by its ID, and a native nonce is unverified, so that charge needs its own verification.
					chargeVerificationDetails: saveCard || isNative ? getVerificationDetails('CHARGE') : null,
					verificationLocationId
				});
			}
		}
	};

	const handleAddCardClick = () => {
		if (onBeforePay && onBeforePay() === false) return;
		if (autoSaveCard) {
			handleNewCard(true);
		} else {
			setSaveCardModalOpen(true);
		}
	};

	const getCardFromUser = async (saveCard) => {
		const squareConfig = getConfig()?.squarePayment;
		// With 3D Secure on, a new card is only saved and paid with afterwards, so the form must not say Pay.
		const onlySavesCard = saveCard && !profile.is_guest && !!getVerificationDetails('STORE')
			&& (!Capacitor.isNativePlatform() || !!getVerificationLocationId());
		const result = await CapSquarePayments.startCardEntry({
			saveButtonTitle: onlySavesCard ? __('Add Card') : `Pay`,
			collectPostalCode: squareConfig?.collectPostalCode || false,
			tintColor: squareConfig?.tintColor || '#000000',
			// Web only: Square runs 3D Secure while tokenizing, so the nonce carries the verification.
			verificationDetails: getVerificationDetails(saveCard ? 'STORE' : 'CHARGE') || undefined
		});
		return result;
	}

	const handleRemoveCard = () => {
		if (removeCardAlert) {
			dispatch(removePaymentCard(removeCardAlert, { __, cb: () => setRemoveCardAlert(null) }));
		}
	};

	return (
		<>
			<div className="square-pay-buttons-container">
				{!hideCardPayment ? (
					<>
						{!profile.is_guest ? (
							<>
								<StrongText className="block text-to-update">{__('Select a payment card')}</StrongText>
								<Spacer size={2} />
								{/* Cards List */}
								{cards && cards.length > 0 ? (
									<>
										<IonList lines="none">
											{/* Ionic moves the items into its own wrapper, so React cannot insert a new card
											    into an existing group. The key rebuilds the group whenever the cards change. */}
											<IonRadioGroup key={cards.map((card) => card.id).join(',')} value={internalSelectedCard}>
												{cards.map((card) => {
													const { id, last4, brand, exp_month, exp_year } = card;

													return (
														<IonItem
															lines="none"
															className="box-content checkout-card cards-list-card"
															key={id}
														>
															<div className="card-icon-wrapper">
																<CardDefault />
															</div>
															<IonLabel
																className="ion-text-wrap"
																onClick={() => handleCardChange(id)}
															>
																<StrongText>{__(brand)} - {exp_month}/{exp_year}</StrongText>
																<Spacer size={0} />
																<StrongText className="block">**** **** **** {last4}</StrongText>
															</IonLabel>
															<IonButton className="remove-card-button" fill="clear" slot="end" onClick={() => setRemoveCardAlert(id)}>
																<IonIcon style={{ height: '20px', width: '20px' }} icon={trashBinOutline} className="remove-card-icon" />
															</IonButton>
															<IonRadio
																color='primary'
																slot="start"
																value={id}
																onIonSelect={() => handleCardChange(id)}
															/>
														</IonItem>
													);
												})}
											</IonRadioGroup>
										</IonList>
										<Spacer size={2} />
									</>) : (
									<div className="no-saved-cards-placeholder">
										<CardDefault className="no-saved-cards-icon" />
										<NormalText>{__('No saved cards available')}</NormalText>
									</div>
								)
								}

								{/* Pay with Selected Card Button */}
								{cards && cards.length > 0 ? (
									<>
										<IonButton
											disabled={disabled || !internalSelectedCard}
											style={{ marginBottom: '10px' }}
											className="button-uppercase"
											onClick={handlePayWithSelectedCard}
											expand="block"
										>
											{getConfig()?.squarePayment?.label || __("Pay")}
										</IonButton>
										<Spacer size={2} />
									</>
								) : null}
							</>
						) :
							<>
								{cards && cards.length == 0 &&
									<div className="no-saved-cards-placeholder">
										<CardDefault className="no-saved-cards-icon" />
										<NormalText>{__('No saved cards available')}</NormalText>
									</div>

								}
							</>

						}

						{/* Add New Card Button */}
						<IonButton
							style={{ marginBottom: '10px' }}
							className="button-uppercase"
							onClick={handleAddCardClick}
							expand="block"
							disabled={disabled}
							fill={cards && cards.length > 0 ? 'clear' : 'solid'}
							color={cards && cards.length > 0 ? 'dark' : 'primary'}
						>
							{profile.is_guest ? __('Pay with Card') : __('Add New Card')}
						</IonButton>
						<Spacer size={1} />
					</>
				) : null}
				{/* Apple Pay Button */}
				{applePayAvailable && (
					<>
						<button
							type="button"
							className="apple-pay-button"
							onClick={handleApplePayClick}
							disabled={disabled}
							aria-label="Apple Pay"
						>
							<svg viewBox="0 0 170 170" className="apple-pay-logo">
								<path fill="#fff" d="M150.4 130.3c-2.4 5.6-5.2 10.8-8.4 15.6-4.4 6.5-7.9 11-10.7 13.5-4.3 4.1-8.8 6.2-13.7 6.4-3.5 0-7.7-1-12.6-3-4.9-2-9.4-3-13.5-3-4.4 0-9 1-14 3-5 2-9 3-12.1 3.1-4.6.2-9.2-1.9-13.8-6.4-3-2.7-6.7-7.3-11.2-13.9-4.8-7.1-8.7-15.3-11.8-24.7-3.3-10.1-5-19.9-5-29.5 0-10.9 2.4-20.3 7.1-28.2 3.7-6.3 8.6-11.3 14.8-15 6.2-3.6 12.8-5.5 20-5.6 3.7 0 8.6 1.1 14.7 3.4 6 2.2 9.9 3.4 11.6 3.4 1.3 0 5.6-1.3 12.9-4 6.9-2.5 12.7-3.5 17.5-3.1 12.9 1 22.6 6.1 29 15.2-11.6 7-17.3 16.8-17.2 29.3.1 9.7 3.6 17.8 10.6 24.2 3.1 3 6.6 5.3 10.5 6.9-.8 2.4-1.7 4.7-2.7 6.9zM119.3 7c0 7.6-2.8 14.8-8.3 21.3-6.6 7.8-14.7 12.3-23.4 11.6-.1-.9-.2-1.9-.2-2.9 0-7.3 3.2-15.2 8.9-21.6 2.8-3.2 6.4-5.9 10.8-8 4.3-2.1 8.4-3.2 12.3-3.4.1 1 .2 2 .2 3z" />
							</svg>
							<span className="apple-pay-text">Pay</span>
						</button>
					</>
				)}

				{/* Google Pay Button - Official Google createButton API */}
				{googlePayAvailable && (
					<div
						ref={renderGooglePayButton}
						className="google-pay-button-container"
						style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
					/>
				)}
			</div>

			<IonAlert
				isOpen={!!removeCardAlert}
				onDidDismiss={() => setRemoveCardAlert(null)}
				message={__('Do you want to remove this card?')}
				buttons={[
					{
						text: __('Cancel'),
						role: 'cancel',
						cssClass: 'secondary link',
						handler: () => setRemoveCardAlert(null),
					},
					{
						text: __('Remove'),
						cssClass: 'primary',
						handler: handleRemoveCard,
					},
				]}
			/>

			{/* Save Card Modal */}
			<Modal isOpen={saveCardModalOpen} onDidDismiss={() => setSaveCardModalOpen(false)}>
				<Title className='modal-title'>{__('Save Card')}</Title>
				<NormalText>{__('Do you want to save this card for future payments?')}</NormalText>
				<Spacer />
				<IonButton
					expand="block"
					color="primary"
					className="button-uppercase"
					onClick={() => handleNewCard(true)}
				>
					{__('Yes')}
				</IonButton>
				<Spacer />
				<IonButton
					expand="block"
					color="medium"
					className="button-uppercase"
					onClick={() => handleNewCard(false)}
				>
					{__('No')}
				</IonButton>
			</Modal>
		</>
	);
};

SquarePayButtons.propTypes = {
	__: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default SquarePayButtons;